import type { ComponentType } from "react";
import {
  AlertTriangle,
  Camera,
  FileUp,
  FolderPlus,
  NotebookPen,
  Plus,
  Receipt
} from "lucide-react";

export type FabIcon = ComponentType<{ className?: string | undefined }>;

export type FabAction = {
  id: string;
  label: string;
  Icon: FabIcon;
  /** In-app path, or a builder that keeps the current query (e.g. tipus=foto). */
  to?: string | ((pathname: string, search: string) => string);
  /** Page-local handler id (see useFabIntent). Used when the action needs the open record. */
  intent?: string;
};

export type FabRoute = {
  id: string;
  /** Trigger aria-label. A single action uses that action's label instead. */
  label: string;
  match: (pathname: string, search: string) => boolean;
  actions: FabAction[];
};

function params(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

/** One list for the mobile FAB. First match wins. At most 4 actions (speed-dial). */
export const FAB_ROUTES: FabRoute[] = [
  {
    id: "ma",
    label: "Gyors rögzítés",
    match: (pathname) => pathname === "/ma",
    actions: [
      { id: "foto", label: "Fotó", Icon: Camera, to: "/dokumentumok?create=1&tipus=foto" },
      { id: "kiadas", label: "Kiadás", Icon: Receipt, to: "/penz/kiadasok?create=1" },
      { id: "naplo", label: "Napló", Icon: NotebookPen, to: "/dokumentumok?create=1&tipus=naplo" },
      {
        id: "problema",
        label: "Probléma",
        Icon: AlertTriangle,
        to: "/dokumentumok?create=1&tipus=naplo&problema=1"
      }
    ]
  },
  {
    id: "kiadasok",
    label: "Új kiadás",
    match: (pathname) => pathname === "/penz/kiadasok" || pathname === "/expenses",
    actions: [
      { id: "uj-kiadas", label: "Új kiadás", Icon: Plus, to: "/penz/kiadasok?create=1" },
      { id: "bizonylat", label: "Bizonylat fotó", Icon: Camera, intent: "expense-receipt" }
    ]
  },
  {
    id: "naplo",
    label: "Új bejegyzés",
    match: (pathname, search) =>
      pathname === "/naplo" ||
      (pathname === "/dokumentumok" && params(search).get("tipus") === "naplo"),
    actions: [
      {
        id: "uj-bejegyzes",
        label: "Új bejegyzés",
        Icon: NotebookPen,
        to: "/dokumentumok?create=1&tipus=naplo"
      }
    ]
  },
  {
    id: "ellenorzesek",
    label: "Ellenőrzés",
    match: (pathname) => pathname === "/ellenorzesek",
    actions: [{ id: "uj-ellenorzes", label: "Ellenőrzés", Icon: Plus, to: "/ellenorzesek?create=1" }]
  },
  {
    id: "dozor",
    label: "Új látogatás",
    match: (pathname) => pathname === "/dozor",
    actions: [{ id: "uj-latogatas", label: "Új látogatás", Icon: Plus, to: "/dozor?create=1" }]
  },
  {
    id: "engedelyek",
    label: "Új engedély",
    match: (pathname) => pathname === "/engedelyek",
    actions: [{ id: "uj-engedely", label: "Új engedély", Icon: Plus, to: "/engedelyek?create=1" }]
  },
  {
    id: "dokumentumok",
    label: "Feltöltés",
    match: (pathname) => pathname === "/dokumentumok" || pathname === "/documents",
    actions: [
      {
        id: "feltoltes",
        label: "Feltöltés",
        Icon: FileUp,
        to: (_pathname, search) => {
          const next = params(search);
          next.set("create", "1");
          next.delete("problema");
          const q = next.toString();
          return `/dokumentumok?${q}`;
        }
      }
    ]
  },
  {
    id: "ajanlatok",
    label: "Új ajánlat",
    match: (pathname) => pathname === "/penz/ajanlatok" || pathname === "/quotes",
    actions: [{ id: "uj-ajanlat", label: "Új ajánlat", Icon: Plus, to: "/penz/ajanlatok?create=1" }]
  },
  {
    id: "letetek",
    label: "Új letét",
    match: (pathname) => pathname === "/penz/letetek",
    actions: [{ id: "uj-letet", label: "Új letét", Icon: Plus, to: "/penz/letetek?create=1" }]
  },
  {
    id: "szamlak",
    label: "Új számla",
    match: (pathname) => pathname === "/penz/szamlak",
    actions: [{ id: "uj-szamla", label: "Új számla", Icon: Plus, to: "/penz/szamlak?create=1" }]
  },
  {
    id: "garancia",
    label: "Új garancia",
    match: (pathname) => pathname === "/penz/garancia",
    actions: [{ id: "uj-garancia", label: "Új garancia", Icon: Plus, to: "/penz/garancia?create=1" }]
  },
  {
    id: "ertekeles",
    label: "Új értékelés",
    match: (pathname) => pathname === "/penz/ertekeles",
    actions: [{ id: "uj-ertekeles", label: "Új értékelés", Icon: Plus, to: "/penz/ertekeles?create=1" }]
  },
  {
    id: "rfq",
    label: "Új RFQ",
    match: (pathname) => pathname === "/penz/rfq",
    actions: [{ id: "uj-rfq", label: "Új RFQ", Icon: Plus, to: "/penz/rfq?create=1" }]
  },
  {
    id: "valtozasok",
    label: "Új változás",
    match: (pathname) => pathname === "/penz/valtozasok",
    actions: [{ id: "uj-valtozas", label: "Új változás", Icon: Plus, to: "/penz/valtozasok?create=1" }]
  },
  {
    id: "kontaktok",
    label: "Új kontakt",
    match: (pathname) => pathname === "/contacts",
    actions: [{ id: "uj-kontakt", label: "Új kontakt", Icon: Plus, to: "/contacts?create=1" }]
  },
  {
    id: "munka",
    label: "Új feladat vagy fázis",
    match: (pathname) => pathname === "/munka",
    actions: [
      { id: "uj-feladat", label: "Új feladat", Icon: Plus, to: "/munka?create=feladat" },
      { id: "uj-fazis", label: "Új fázis", Icon: FolderPlus, to: "/munka?create=fazis" }
    ]
  },
  {
    id: "fazis",
    label: "Új feladat",
    match: (pathname) => /^\/phases\/[^/]+$/.test(pathname),
    actions: [{ id: "uj-feladat", label: "Új feladat", Icon: Plus, intent: "phase-new-task" }]
  },
  {
    id: "feladat",
    label: "Új kiadás",
    match: (pathname) => /^\/phases\/[^/]+\/tasks\/[^/]+$/.test(pathname),
    actions: [{ id: "uj-kiadas", label: "Új kiadás", Icon: Receipt, intent: "task-new-expense" }]
  }
];

export function fabForLocation(pathname: string, search: string): FabRoute | null {
  return FAB_ROUTES.find((route) => route.match(pathname, search)) ?? null;
}

/** Header create buttons stay from this width up. The FAB is only below it. */
export const FAB_HEADER_MQ = "(min-width: 768px)";
